import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Separator } from "@/components/ui/separator";
import { Progress } from "@/components/ui/progress";
import { 
  Lightbulb, 
  TrendingUp, 
  Target, 
  RefreshCw,
  ThumbsUp,
  ThumbsDown,
  Eye,
  Plus,
  Brain,
  Users,
  BookOpen
} from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";

interface ContentRecommendation {
  id: string;
  type: "create" | "improve" | "update" | "remove";
  priority: "high" | "medium" | "low";
  title: string;
  description: string;
  reason: string;
  confidence: number;
  estimatedImpact: string;
  category: string;
  affectedContent?: string[];
  suggestedActions: string[];
  metrics?: {
    currentPerformance?: number;
    projectedImprovement?: number;
  };
}

export default function ContentRecommendations() {
  const { toast } = useToast();
  const [filterPriority, setFilterPriority] = useState("all");
  const [filterType, setFilterType] = useState("all");
  const [isGenerating, setIsGenerating] = useState(false);

  const [recommendations, setRecommendations] = useState<ContentRecommendation[]>([
    {
      id: "1",
      type: "create",
      priority: "high",
      title: "Create Advanced Cardiology MCQs",
      description: "Users are performing well on basic cardiology questions but lack advanced-level content",
      reason: "Gap analysis shows 68% of users need more challenging cardiology content",
      confidence: 89,
      estimatedImpact: "Increase engagement by 25%",
      category: "Content Gap",
      suggestedActions: [
        "Create 15-20 advanced cardiology MCQs",
        "Focus on complex arrhythmias and interventional procedures",
        "Include case-based scenarios"
      ],
      metrics: {
        currentPerformance: 72,
        projectedImprovement: 25
      }
    },
    {
      id: "2",
      type: "improve",
      priority: "high",
      title: "Enhance Pharmacology Explanations",
      description: "Pharmacology questions have high incorrect rates and negative feedback",
      reason: "Average score: 58%, 23% negative feedback on explanations",
      confidence: 92,
      estimatedImpact: "Improve scores by 15-20%",
      category: "Quality Issue",
      affectedContent: ["pharm_001", "pharm_015", "pharm_023"],
      suggestedActions: [
        "Add mechanism of action diagrams",
        "Include drug interaction warnings",
        "Provide clinical context examples"
      ],
      metrics: {
        currentPerformance: 58,
        projectedImprovement: 18
      }
    },
    {
      id: "3",
      type: "update",
      priority: "medium",
      title: "Update COVID-19 Treatment Guidelines",
      description: "Treatment protocols have changed based on latest evidence",
      reason: "Content created 8 months ago, new guidelines published",
      confidence: 95,
      estimatedImpact: "Maintain medical accuracy",
      category: "Outdated Content",
      affectedContent: ["covid_treat_001", "covid_treat_005"],
      suggestedActions: [
        "Update antiviral recommendations",
        "Revise isolation guidelines",
        "Include new variant information"
      ]
    },
    {
      id: "4",
      type: "create",
      priority: "medium",
      title: "Add Pediatric Dosing Calculator",
      description: "Users frequently struggle with pediatric medication dosing",
      reason: "Low performance on pediatric pharmacology (avg: 61%)",
      confidence: 76,
      estimatedImpact: "Reduce errors by 30%",
      category: "Learning Tool",
      suggestedActions: [
        "Create interactive dosing calculator",
        "Include weight-based formulas",
        "Add safety warnings and checks"
      ],
      metrics: {
        currentPerformance: 61,
        projectedImprovement: 30
      }
    },
    {
      id: "5",
      type: "remove",
      priority: "low",
      title: "Remove Redundant Anatomy Content",
      description: "Multiple overlapping anatomy questions causing confusion",
      reason: "Duplicate content identified, low engagement scores",
      confidence: 71,
      estimatedImpact: "Improve content clarity",
      category: "Content Optimization",
      affectedContent: ["anat_007", "anat_012", "anat_018"],
      suggestedActions: [
        "Consolidate similar questions",
        "Keep highest-performing versions",
        "Redirect references to consolidated content"
      ]
    }
  ]);

  const generateRecommendations = async () => {
    setIsGenerating(true);
    
    try {
      const { data, error } = await supabase.functions.invoke('ai-content-recommendations', {
        body: {
          analysisType: 'comprehensive',
          includeUserData: true
        }
      });

      if (error) throw error;
      
      // In a real implementation, you would update recommendations with the response
      toast({
        title: "Recommendations Updated",
        description: `Generated ${data.newRecommendations} new recommendations.`,
      });
      
    } catch (error) {
      console.error('Error generating recommendations:', error);
      toast({
        title: "Generation Failed",
        description: "Failed to generate new recommendations. Please try again.",
        variant: "destructive"
      });
    } finally {
      setIsGenerating(false);
    }
  };

  const implementRecommendation = (id: string) => {
    const recommendation = recommendations.find(r => r.id === id);
    toast({
      title: "Implementation Started",
      description: `Started implementing: ${recommendation?.title}`,
    });
    
    // In a real implementation, this would trigger the appropriate workflow
  };

  const dismissRecommendation = (id: string) => {
    setRecommendations(prev => prev.filter(r => r.id !== id));
    toast({
      title: "Recommendation Dismissed",
      description: "The recommendation has been removed.",
    });
  };

  const filteredRecommendations = recommendations.filter(rec => {
    const priorityMatch = filterPriority === "all" || rec.priority === filterPriority;
    const typeMatch = filterType === "all" || rec.type === filterType;
    return priorityMatch && typeMatch;
  });

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case "high": return "destructive";
      case "medium": return "default";
      case "low": return "secondary";
      default: return "outline";
    }
  };

  const getTypeIcon = (type: string) => {
    switch (type) {
      case "create": return <Plus className="h-4 w-4" />;
      case "improve": return <TrendingUp className="h-4 w-4" />;
      case "update": return <RefreshCw className="h-4 w-4" />;
      case "remove": return <Target className="h-4 w-4" />;
      default: return <Lightbulb className="h-4 w-4" />;
    }
  };

  const getTypeColor = (type: string) => {
    switch (type) {
      case "create": return "text-success";
      case "improve": return "text-primary";
      case "update": return "text-warning";
      case "remove": return "text-destructive";
      default: return "text-muted-foreground";
    }
  };

  const getConfidenceColor = (confidence: number) => {
    if (confidence >= 90) return "text-success";
    if (confidence >= 75) return "text-primary";
    if (confidence >= 60) return "text-warning";
    return "text-destructive";
  };

  const aggregateStats = {
    totalRecommendations: recommendations.length,
    highPriority: recommendations.filter(r => r.priority === "high").length,
    contentGaps: recommendations.filter(r => r.category === "Content Gap").length,
    qualityIssues: recommendations.filter(r => r.category === "Quality Issue").length,
    avgConfidence: Math.round(recommendations.reduce((acc, r) => acc + r.confidence, 0) / recommendations.length)
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h3 className="text-lg font-medium">Content Recommendations</h3>
          <p className="text-sm text-muted-foreground">
            AI-powered suggestions to improve your content library and user experience
          </p>
        </div>
        <Button onClick={generateRecommendations} disabled={isGenerating} className="flex items-center gap-2">
          <Brain className={`h-4 w-4 ${isGenerating ? 'animate-spin' : ''}`} />
          {isGenerating ? "Generating..." : "Refresh Recommendations"}
        </Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm flex items-center gap-2">
              <Lightbulb className="h-4 w-4" />
              Total Recommendations
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{aggregateStats.totalRecommendations}</div>
            <p className="text-xs text-muted-foreground">Active suggestions</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm flex items-center gap-2">
              <Target className="h-4 w-4" />
              High Priority
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-destructive">{aggregateStats.highPriority}</div>
            <p className="text-xs text-muted-foreground">Require immediate attention</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm flex items-center gap-2">
              <BookOpen className="h-4 w-4" />
              Content Gaps
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-warning">{aggregateStats.contentGaps}</div>
            <p className="text-xs text-muted-foreground">Missing content areas</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm flex items-center gap-2">
              <Brain className="h-4 w-4" />
              Avg Confidence
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className={`text-2xl font-bold ${getConfidenceColor(aggregateStats.avgConfidence)}`}>
              {aggregateStats.avgConfidence}%
            </div>
            <p className="text-xs text-muted-foreground">AI prediction accuracy</p>
          </CardContent>
        </Card>
      </div>

      <div className="flex gap-4">
        <Select value={filterPriority} onValueChange={setFilterPriority}>
          <SelectTrigger className="w-48">
            <SelectValue placeholder="Filter by priority" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Priorities</SelectItem>
            <SelectItem value="high">High Priority</SelectItem>
            <SelectItem value="medium">Medium Priority</SelectItem>
            <SelectItem value="low">Low Priority</SelectItem>
          </SelectContent>
        </Select>

        <Select value={filterType} onValueChange={setFilterType}>
          <SelectTrigger className="w-48">
            <SelectValue placeholder="Filter by type" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Types</SelectItem>
            <SelectItem value="create">Create Content</SelectItem>
            <SelectItem value="improve">Improve Content</SelectItem>
            <SelectItem value="update">Update Content</SelectItem>
            <SelectItem value="remove">Remove Content</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="space-y-4">
        {filteredRecommendations.map((recommendation) => (
          <Card key={recommendation.id}>
            <CardHeader>
              <div className="flex items-start justify-between">
                <div className="flex items-start gap-3">
                  <div className={`mt-1 ${getTypeColor(recommendation.type)}`}>
                    {getTypeIcon(recommendation.type)}
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2">
                      <h4 className="font-medium">{recommendation.title}</h4>
                      <Badge variant={getPriorityColor(recommendation.priority) as any}>
                        {recommendation.priority}
                      </Badge>
                      <Badge variant="outline" className="text-xs">
                        {recommendation.category}
                      </Badge>
                      <span className={`text-sm ${getConfidenceColor(recommendation.confidence)}`}>
                        {recommendation.confidence}% confidence
                      </span>
                    </div>
                    <p className="text-sm text-muted-foreground mb-2">
                      {recommendation.description}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      <strong>Reason:</strong> {recommendation.reason}
                    </p>
                  </div>
                </div>
                <div className="flex gap-2">
                  <Button size="sm" onClick={() => implementRecommendation(recommendation.id)}>
                    <ThumbsUp className="h-3 w-3 mr-1" />
                    Implement
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => dismissRecommendation(recommendation.id)}>
                    <ThumbsDown className="h-3 w-3 mr-1" />
                    Dismiss
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <h5 className="text-sm font-medium mb-2">Suggested Actions:</h5>
                  <ul className="space-y-1">
                    {recommendation.suggestedActions.map((action, index) => (
                      <li key={index} className="text-sm text-muted-foreground flex items-start gap-2">
                        <span className="text-primary">•</span>
                        {action}
                      </li>
                    ))}
                  </ul>
                </div>

                <div className="space-y-3">
                  <div>
                    <div className="flex justify-between text-sm mb-1">
                      <span>Estimated Impact</span>
                      <span className="font-medium">{recommendation.estimatedImpact}</span>
                    </div>
                  </div>

                  {recommendation.metrics && (
                    <div className="space-y-2">
                      {recommendation.metrics.currentPerformance && (
                        <div>
                          <div className="flex justify-between text-xs mb-1">
                            <span>Current Performance</span>
                            <span>{recommendation.metrics.currentPerformance}%</span>
                          </div>
                          <Progress value={recommendation.metrics.currentPerformance} className="h-1" />
                        </div>
                      )}

                      {recommendation.metrics.projectedImprovement && (
                        <div className="text-xs text-success">
                          <span>Projected improvement: +{recommendation.metrics.projectedImprovement}%</span>
                        </div>
                      )}
                    </div>
                  )}

                  {recommendation.affectedContent && (
                    <div>
                      <div className="text-xs text-muted-foreground mb-1">
                        Affects {recommendation.affectedContent.length} content item(s)
                      </div>
                      <Button size="sm" variant="ghost" className="h-6 px-2 text-xs">
                        <Eye className="h-3 w-3 mr-1" />
                        View Items
                      </Button>
                    </div>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {filteredRecommendations.length === 0 && (
        <Card>
          <CardContent className="text-center py-8">
            <Lightbulb className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
            <h3 className="font-medium mb-2">No Recommendations Found</h3>
            <p className="text-sm text-muted-foreground mb-4">
              Try adjusting your filters or generate new recommendations.
            </p>
            <Button onClick={generateRecommendations}>
              Generate Recommendations
            </Button>
          </CardContent>
        </Card>
      )}
    </div>
  );
}